import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Lightbulb, Flag, Sparkles, Volume2, Square } from 'lucide-react';
import type { Question } from '@/types/adaptive';
import { HighlightedText } from './HighlightedText';
import { QuestionStemHighlighter } from './QuestionStemHighlighter';

/** Narration wiring, computed by the page (the card stays dumb). Absent →
 *  no speaker button, byte-identical render to the pre-narration card. */
export interface QuestionNarrationProps {
  isPlaying: boolean;
  onToggle: () => void;
  stemToken: number | null;
  optionToken: (letter: string) => number | null;
  isRowBeingRead: (letter: string) => boolean;
}

interface QuestionCardProps {
  question: Question;
  selectedAnswer: string;
  onAnswerSelect: (answer: string) => void;
  showHint: boolean;
  onToggleHint: () => void;
  showingFeedback?: boolean;
  confidence: number | null;
  onConfidenceChange: (level: number | null) => void;
  flaggedForReview: boolean;
  onToggleFlagForReview: () => void;
  narration?: QuestionNarrationProps;
  /** 'onboarding' is the signed-out diagnostic: the same card without the hint,
   *  Ask Pam, confidence and flag chrome, which all need an account. */
  variant?: 'quiz' | 'onboarding';
}

/**
 * The .pen "Quiz Player (Question)" surface (node 3mLdc): a teal category chip
 * with Show-hint + Ask-Pam actions, the Fraunces question, lettered ledge answer
 * cards (selected = teal ring + filled letter badge), the 1–5 confidence scale,
 * and a flag-for-later row. Confidence + flag stay in the QuizUI store and ride
 * the existing submit path — no behaviour changed, only the surface.
 */
export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  selectedAnswer,
  onAnswerSelect,
  showHint,
  onToggleHint,
  showingFeedback = false,
  confidence,
  onConfidenceChange,
  flaggedForReview,
  onToggleFlagForReview,
  narration,
  variant = 'quiz',
}) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={question.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="space-y-5"
    >
      {/* Category chip + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-primary-50 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-primary-700">
            {question.category || 'Practice Question'}
          </span>
          {/* Listen — Pam reads the question in her own voice, the current word
              highlighted as she goes. Sits next to the category so it reads as
              part of the question, not chrome (same placement as iOS). */}
          {narration && (
            <button
              type="button"
              onClick={narration.onToggle}
              aria-label={narration.isPlaying ? 'Stop reading' : 'Read question aloud'}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                narration.isPlaying
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-neutral-200 bg-white text-neutral-500 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {narration.isPlaying ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          )}
        </span>

        {variant === 'quiz' && (
        <div className="flex items-center gap-2">
          {question.hint && (
            <button
              onClick={onToggleHint}
              className={`inline-flex items-center gap-1.5 rounded-2xl border-2 px-3 py-2 text-[13px] font-bold transition-colors ${
                showHint
                  ? 'border-warning bg-warning-light text-warning-dark'
                  : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-warning hover:text-warning-dark'
              }`}
            >
              <Lightbulb className="w-4 h-4" />
              {showHint ? 'Hide hint' : 'Show hint'}
            </button>
          )}

          <Link
            to="/ask-pam"
            state={{ fromQuestion: { text: question.question, category: question.category } }}
            className="inline-flex items-center gap-1.5 rounded-2xl border-2 border-accent-200 bg-accent-50 px-3 py-2 text-[13px] font-extrabold text-accent-500 transition-colors hover:bg-accent-100"
          >
            <Sparkles className="w-4 h-4" />
            Ask Pam
            <span className="rounded-full bg-accent-500 px-1.5 py-0.5 text-[9px] font-extrabold leading-none text-white">
              NEW
            </span>
          </Link>
        </div>
        )}
      </div>

      {/* Hint */}
      {showHint && question.hint && (
        <div className="rounded-r-xl border-l-4 border-warning bg-warning-light p-4">
          <p className="text-sm font-medium text-warning-dark">
            <strong>Hint:</strong> {question.hint}
          </p>
        </div>
      )}

      {/* Question — Fraunces. Word-level gold wash follows Pam's narration;
          select any text to mark it (ASWB/Prometric-style highlighter),
          persisted for this question for the rest of the session. */}
      <h2 className="font-display font-semibold text-xl sm:text-2xl text-neutral-900 leading-[1.35]">
        <QuestionStemHighlighter
          questionId={question.id}
          text={question.question}
          token={narration?.stemToken ?? null}
        />
      </h2>

      {/* Answer options — lettered ledge cards */}
      <div className="space-y-2.5">
        {/* Render only the options this question actually has, so 3-option (2026
            format) questions render 3 cards — not a dead 4th "[Option not available]"
            button. Letters stay A/B/C… in order of the present options. */}
        {(['A', 'B', 'C', 'D'] as const)
          .filter((option) => !!(question[`option_${option.toLowerCase()}` as keyof typeof question] as string | undefined))
          .map((option) => {
          const answerKey = `option_${option.toLowerCase()}` as keyof typeof question;
          const answerText = question[answerKey] as string;
          const displayText = answerText;
          const isDisabled = showingFeedback;
          const isSelected = selectedAnswer === option;
          // Narration: gold ring while Pam is anywhere in this option (incl.
          // the "Option B." beat), word-level wash on the token she's saying.
          const isBeingRead = narration?.isRowBeingRead(option) ?? false;
          const spokenToken = narration?.optionToken(option) ?? null;

          return (
            <label
              key={option}
              data-testid="question-option"
              data-option={option}
              className={`group flex items-center gap-3.5 rounded-[20px] border-2 p-4 transition-colors ${
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : isBeingRead
                    ? 'border-celebrate-500 bg-white'
                    : showingFeedback
                      ? 'border-neutral-200 bg-white opacity-60'
                      : 'border-neutral-200 bg-white hover:border-primary-300 hover:bg-primary-50/50'
              }`}
            >
              <input
                type="radio"
                name="answer"
                value={option}
                checked={isSelected}
                onChange={(e) => !showingFeedback && onAnswerSelect(e.target.value)}
                disabled={isDisabled}
                className="sr-only"
              />
              {/* Letter badge */}
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[10px] text-[13px] font-extrabold transition-colors ${
                  isSelected
                    ? 'bg-primary-500 text-white'
                    : 'bg-neutral-50 text-neutral-600 group-hover:bg-primary-100'
                }`}
              >
                {option}
              </span>
              <span
                className={`text-[15px] leading-snug ${
                  isSelected ? 'font-extrabold text-primary-700' : 'font-bold text-neutral-900'
                } ${!answerText ? 'italic text-neutral-400' : ''}`}
              >
                <HighlightedText text={displayText} token={spokenToken} />
              </span>
            </label>
          );
        })}
      </div>

      {/* Confidence scale + flag — mirror the .pen, kept out of the feedback view */}
      {!showingFeedback && variant === 'quiz' && (
        <div className="space-y-4 border-t border-neutral-200 pt-5">
          {/* Confidence */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="text-[15px] font-extrabold text-neutral-900">
                How confident are you?
              </label>
              {confidence !== null && (
                <button
                  onClick={() => onConfidenceChange(null)}
                  className="text-[13px] font-bold text-primary-700 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  onClick={() => onConfidenceChange(level)}
                  aria-pressed={confidence === level}
                  aria-label={`Confidence ${level} of 5`}
                  className={`h-[42px] flex-1 rounded-2xl text-[15px] font-extrabold transition-colors ${
                    confidence === level
                      ? 'bg-primary-500 text-white'
                      : 'border-2 border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs font-bold text-neutral-600">
              <span>Not sure</span>
              <span>Very confident</span>
            </div>
          </div>

          {/* Flag for later review */}
          <div className="flex items-center justify-between rounded-[20px] bg-neutral-50 p-3.5">
            <span className="text-sm font-bold text-neutral-900">Flag for later review</span>
            <button
              onClick={onToggleFlagForReview}
              aria-pressed={flaggedForReview}
              className={`inline-flex items-center gap-1.5 rounded-2xl px-3.5 py-2 text-[13px] font-extrabold transition-colors ${
                flaggedForReview
                  ? 'bg-secondary-500 text-white'
                  : 'bg-secondary-50 text-secondary-700 hover:bg-secondary-100'
              }`}
            >
              <Flag className="w-4 h-4" />
              {flaggedForReview ? 'Flagged' : 'Flag'}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  </AnimatePresence>
);
